import React, { useState, useEffect, useRef } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Upload, Download, Trash2, File, Image, FileSpreadsheet, FileType } from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const DocumentsPage = () => {
  const { can } = usePermissions();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterProject, setFilterProject] = useState("all");
  const [filterClient, setFilterClient] = useState("all");
  const [uploadProject, setUploadProject] = useState("none");
  const [uploadClient, setUploadClient] = useState("none");
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterProject && filterProject !== "all") params.project_id = filterProject;
      if (filterClient && filterClient !== "all") params.client_id = filterClient;

      const [docsRes, projectsRes, clientsRes] = await Promise.all([
        API.get("/documents", { params }),
        API.get("/projects"),
        API.get("/clients"),
      ]);
      setDocuments(docsRes.data);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterProject, filterClient]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    if (uploadProject && uploadProject !== "none") formData.append("project_id", uploadProject);
    if (uploadClient && uploadClient !== "none") formData.append("client_id", uploadClient);

    try {
      await API.post("/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/documents/${doc.doc_id}`);
      const { file_data, filename, file_type } = response.data;
      
      // Create download link
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await API.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith("image/")) return Image;
    if (fileType?.includes("spreadsheet") || fileType?.includes("excel")) return FileSpreadsheet;
    if (fileType?.includes("pdf")) return FileType;
    return File;
  };

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.project_id === projectId);
    return project?.name || "-";
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.client_id === clientId);
    return client?.name || "-";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return <div className="animate-pulse">Loading documents...</div>;
  }

  return (
    <div data-testid="documents-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Documents</h1>
          <p className="text-slate-400 font-ibm mt-1">Store and manage project files</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-40" data-testid="filter-project-select">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.project_id} value={project.project_id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-40" data-testid="filter-client-select">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.client_id} value={client.client_id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Upload Section */}
      {can('upload_documents') && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h3 className="font-chivo font-semibold text-lg text-white mb-4">Upload Document</h3>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-400 mb-1 block">Link to Project (optional)</label>
              <Select value={uploadProject} onValueChange={setUploadProject}>
                <SelectTrigger data-testid="upload-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-400 mb-1 block">Link to Client (optional)</label>
              <Select value={uploadClient} onValueChange={setUploadClient}>
                <SelectTrigger data-testid="upload-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUpload}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                data-testid="upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-white"
              >
                <Upload size={18} className="mr-2" />
                {uploading ? "Uploading..." : "Upload File"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No documents yet</h3>
          <p className="text-slate-400 mb-4">Upload your first document to get started</p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">File</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Project</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Client</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Uploaded</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {documents.map((doc) => {
                const FileIcon = getFileIcon(doc.file_type);
                return (
                  <tr key={doc.doc_id} data-testid={`doc-row-${doc.doc_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                          <FileIcon size={20} className="text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{doc.filename}</p>
                          <p className="text-xs text-slate-400">{doc.file_type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {getProjectName(doc.project_id)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {getClientName(doc.client_id)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`download-doc-${doc.doc_id}`}
                          onClick={() => handleDownload(doc)}
                        >
                          <Download size={14} className="mr-1" />
                          Download
                        </Button>
                        {can('delete_documents') && (
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`delete-doc-${doc.doc_id}`}
                            onClick={() => handleDelete(doc.doc_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
